import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { SidebarNavItem } from '@/shared/utils/sidebar-nav';
import { useAccess } from '@/shared/access';
import { cn } from '@/shared/utils/cn';
import { NavIcon } from './nav-icon';
import { navLinkClass } from './nav-link-class';
import { SidebarNavLink } from './sidebar-nav-link';

/**
 * A collapsible sidebar group (e.g. Inventory → Projects, Properties). The row
 * expands and collapses; the links inside it navigate. It opens by itself when
 * one of its pages is the current one, and reads as active while closed so the
 * user can still see where they are.
 */
export function SidebarNavGroup({ item, collapsed = false }: { item: SidebarNavItem; collapsed?: boolean }) {
    const { pathname } = useLocation();
    const { can } = useAccess();
    const children = item.children ?? [];
    const childActive = children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`));

    const [open, setOpen] = useState(childActive);
    useEffect(() => { if (childActive) setOpen(true); }, [childActive]);

    // Plan-locked (an owner whose plan lacks the module): one locked row that
    // opens the Upgrade popup — the same treatment a single link gets.
    if (item.feature && !can(item.feature).allowed) {
        return <SidebarNavLink item={item} collapsed={collapsed} />;
    }

    // Icon rail: no room for children, so one icon stands in for the group and
    // goes to its first page.
    if (collapsed) {
        return (
            <NavLink
                to={children[0]?.to ?? item.to}
                className={navLinkClass({ isActive: childActive, collapsed })}
                title={item.label}
                aria-label={item.label}
            >
                <NavIcon icon={item.icon} isActive={childActive} />
            </NavLink>
        );
    }

    const highlighted = childActive && !open;
    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className={navLinkClass({ isActive: highlighted })}
            >
                <NavIcon icon={item.icon} isActive={highlighted} />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronDown className={cn('h-3.5 w-3.5 text-ink-500 transition-transform', !open && '-rotate-90')} />
            </button>
            {open && (
                <ul className="mt-0.5 space-y-0.5">
                    {children.map((c) => (
                        <li key={c.to + c.label}>
                            <SidebarNavLink item={c} nested />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
